<template>
  <div class="musubi-page musubi-page--chfeed">
    <header class="musubi-page__head">
      <h1>Companies House feed <span class="plan-pill">Day-zero</span></h1>
      <p class="text-muted">
        Companies that match your targeting the day they appear on the Companies House register — new
        incorporations and status changes in the sectors and regions you sell to. Reach a brand-new firm
        before anyone else has, and add the promising ones straight to a pipeline.
      </p>
    </header>

    <!-- ── Gated (Starter) teaser: violet plan-gate. Skeleton rows (never
         fabricated data) blur behind the upgrade panel. `gateLocked` is null
         while /me/usage loads (render nothing yet). ── -->
    <div v-if="gateLocked" class="ch-teaser">
      <div class="ch-teaser__blur" aria-hidden="true">
        <div class="ch-skel-tbl">
          <div v-for="n in 4" :key="n" class="ch-skel-row">
            <span class="skel" style="width:34%"></span>
            <span class="skel" style="width:14%"></span>
            <span class="skel" style="width:12%"></span>
            <span class="skel" style="width:10%"></span>
            <span class="skel" style="width:20%"></span>
          </div>
        </div>
      </div>
      <div class="ch-teaser__panel">
        <div class="upgrade">
          <div class="upgrade__ic"><CygnusIcon name="building" /></div>
          <div class="upgrade__eyebrow">Available on Growth</div>
          <h3>Reach new companies on day one</h3>
          <p>
            The Companies House feed surfaces firms the moment they match your targeting on the register —
            before anyone's had a chance to reach them. It's included on the <strong>Growth</strong> plan.
          </p>
          <div class="upgrade__benefits">
            <div class="benefit"><CygnusIcon name="check" /> Day-zero alerts when a company in your sector &amp; region incorporates</div>
            <div class="benefit"><CygnusIcon name="check" /> Status-change monitoring on the firms you already care about</div>
            <div class="benefit"><CygnusIcon name="check" /> Add a fresh company straight to a pipeline or drip flow</div>
          </div>
          <div class="upgrade__actions">
            <router-link to="/admin/usage-plans" class="btn ch-btn-violet">Upgrade to Growth</router-link>
            <router-link to="/admin/usage-plans" class="ch-compare">Compare plans <CygnusIcon name="arrow-right" /></router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Entitled page ── -->
    <template v-else-if="gateLocked === false">
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

      <!-- Operator-only: key management + stream health (Ops::PLATFORM) -->
      <section v-if="isOperator && health" class="ch-ops">
        <div class="ch-ops__card">
          <div class="ch-ops__head">
            <CygnusIcon name="lock" />
            <span>Stream key</span>
            <span class="ch-ops__badge" :class="health.key_configured ? 'on' : 'off'">
              {{ health.key_configured ? 'Configured' : 'Not configured' }}
            </span>
          </div>
          <div class="ch-ops__body">
            <div class="ch-ops__row">
              <span class="ch-ops__k">Current key</span>
              <span class="ch-ops__v">
                <code v-if="health.key_masked">{{ health.key_masked }}</code>
                <span v-else class="text-muted">— none —</span>
                <span v-if="health.key_source" class="ch-src">{{ health.key_source === 'db' ? 'set here' : 'from environment' }}</span>
              </span>
            </div>
            <div class="ch-ops__setline">
              <input v-model="keyInput" type="password" autocomplete="off" class="ch-input"
                placeholder="Paste a new streaming key…" style="flex:1;min-width:180px">
              <button class="btn ch-btn-primary btn-sm" :disabled="opBusy || !keyInput.trim()" @click="saveKey">
                <CygnusIcon name="download" /> {{ health.has_db_override ? 'Rotate' : 'Set key' }}
              </button>
              <button v-if="health.has_db_override" class="btn ch-btn-secondary btn-sm" :disabled="opBusy" @click="clearKey">
                Clear override
              </button>
            </div>
            <p class="ch-ops__note">
              Stored encrypted; the value is never shown again. An operator override wins over the
              <code>MUSUBI_CH_STREAM_KEY</code> environment key — clearing it falls back to the environment.
            </p>
            <p v-if="opMessage" class="alert alert-success py-1 small mb-0">{{ opMessage }}</p>
          </div>
        </div>
        <div class="ch-ops__card">
          <div class="ch-ops__head"><CygnusIcon name="heart" /><span>Stream health</span></div>
          <div class="ch-ops__grid">
            <div class="ch-health"><div class="ch-health__k">Last cron run</div><div class="ch-health__v">{{ health.last_run ? relTime(health.last_run) : '—' }}</div></div>
            <div class="ch-health"><div class="ch-health__k">Last timepoint</div><div class="ch-health__v">{{ health.last_timepoint || '—' }}</div></div>
            <div class="ch-health"><div class="ch-health__k">Events processed</div><div class="ch-health__v">{{ health.events_processed.toLocaleString() }}</div></div>
            <div class="ch-health"><div class="ch-health__k">Signals raised</div><div class="ch-health__v">{{ health.signals_raised.toLocaleString() }}</div></div>
          </div>
        </div>
      </section>

      <div class="coverage-note">
        <CygnusIcon name="info-circle" />
        <span>
          Signals only appear for companies matching <strong>your fit criteria</strong> (SIC sector + region).
          Set your targeting narrow and this feed stays quiet and relevant; widen it for more volume. We
          surface the register event — a brand-new company usually has <strong>no website or contacts yet</strong>,
          so treat these as first-mover leads, not enriched records.
        </span>
      </div>

      <section class="ch-kpis">
        <MusubiKpiCard label="New incorporations · 7d" :value="kpis.new_incorporations_7d" delta="matching your targeting" delta-dir="up" icon="building" />
        <MusubiKpiCard label="Status changes · 7d" :value="kpis.status_changes_7d" delta="on companies you track" icon="refresh" />
        <MusubiKpiCard label="Actioned" :value="counts.actioned" icon="check-circle" />
        <MusubiKpiCard label="Avg age when surfaced" :value="avgAgeDisplay" :delta="kpis.avg_age_days !== null ? 'day-zero reach' : ''" delta-dir="up" icon="clock" />
      </section>

      <div class="ch-tabs">
        <button v-for="t in tabs" :key="t.key" class="ch-tab" :class="{ active: tab === t.key }" @click="setTab(t.key)">
          {{ t.label }}<span v-if="counts[t.countKey]" class="count">{{ counts[t.countKey] }}</span>
        </button>
      </div>

      <div class="ch-toolbar">
        <select v-model="filters.event_type" class="ch-select" @change="load">
          <option value="">Any event</option>
          <option value="incorporated">Incorporations only</option>
          <option value="status_change">Status changes only</option>
        </select>
        <select v-model="filters.sector" class="ch-select">
          <option value="">Any sector</option>
          <option v-for="s in sectorOptions" :key="s" :value="s">{{ s }}</option>
        </select>
        <select v-model="filters.region" class="ch-select">
          <option value="">Any region</option>
          <option v-for="r in regionOptions" :key="r" :value="r">{{ r }}</option>
        </select>
        <input v-model="filters.search" class="ch-input" placeholder="Search name or company no…" style="width:200px" @keyup.enter="load" @blur="load">
      </div>

      <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

      <div v-if="loading" class="ch-loading">Loading Companies House signals…</div>

      <!-- (b) No targeting set — the feed is empty by design -->
      <div v-else-if="!hasCriteria" class="dc-empty teal-edge">
        <div class="dc-empty__icon teal"><CygnusIcon name="pin" /></div>
        <div class="dc-empty__title">Tell us who you're looking for</div>
        <div class="dc-empty__hint">
          The Companies House feed is empty because you haven't set any fit criteria yet. We only raise a
          signal when a new or changed company matches the <strong>sectors and regions you target</strong> —
          no targeting means no day-zero noise, on purpose.
        </div>
        <div class="dc-empty__why">
          <div class="row"><CygnusIcon name="building" /> Pick the <strong>SIC sectors</strong> you sell into (e.g. accountancy, legal, property) — we match new incorporations against them.</div>
          <div class="row"><CygnusIcon name="pin" /> Add a <strong>region</strong> to keep it local — e.g. Gloucestershire, or the wider South West for more volume.</div>
          <div class="row"><CygnusIcon name="clock" /> Once set, matching firms appear here the day they hit the register — usually within 24 hours.</div>
        </div>
        <div class="dc-empty__actions">
          <router-link to="/acquire/targeting" class="btn ch-btn-primary"><CygnusIcon name="pin" /> Set your targeting</router-link>
        </div>
      </div>

      <!-- (c) Targeting set, feed connected — nothing matched yet -->
      <div v-else-if="!visibleRows.length" class="dc-empty">
        <div class="dc-empty__icon"><CygnusIcon name="building" /></div>
        <div class="dc-empty__title">{{ rows.length ? 'No companies match these filters' : 'No matching companies yet' }}</div>
        <div class="dc-empty__hint">
          Your targeting is set and the Companies House stream is connected — we just haven't seen a new or
          changed company match your criteria yet. That's normal for narrow targeting.
        </div>
        <div class="dc-empty__why">
          <div class="row"><CygnusIcon name="clock" /> UK-wide incorporations matching a <strong>narrow sector + a single county</strong> can be genuinely sparse — some days there simply aren't any.</div>
          <div class="row"><CygnusIcon name="building" /> Widen the net for more volume: add adjacent <strong>sectors</strong> or expand the <strong>region</strong>.</div>
          <div class="row"><CygnusIcon name="bell" /> We check the register daily — you'll get a bell digest the moment a match appears, so there's nothing to watch here.</div>
        </div>
        <div class="dc-empty__actions">
          <router-link to="/acquire/targeting" class="btn ch-btn-outline"><CygnusIcon name="gear" /> Adjust targeting</router-link>
        </div>
      </div>

      <!-- Table -->
      <div v-else class="ch-tbl">
        <table>
          <thead>
            <tr>
              <th style="width:30px"></th>
              <th>Company</th>
              <th>Event</th>
              <th>Sector</th>
              <th>Region</th>
              <th>Why it matched</th>
              <th>Age</th>
              <th style="text-align:right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="r in visibleRows" :key="r.signal_id">
              <tr :class="{ 'is-open': expanded === r.signal_id }">
                <td><button class="chev" @click="toggleExpand(r.signal_id)"><CygnusIcon name="chevron-right" /></button></td>
                <td>
                  <div class="co-name">{{ r.name }}</div>
                  <div class="co-meta">
                    <span class="num">{{ r.company_number }}</span>
                    <span class="sep">·</span>
                    <span>{{ incorpLabel(r) }}</span>
                  </div>
                </td>
                <td>
                  <span class="ev-badge" :class="r.event_type === 'incorporated' ? 'incorporated' : 'status'">
                    <span class="dot"></span>{{ r.event_type === 'incorporated' ? 'Incorporated' : 'Status change' }}
                  </span>
                  <div v-if="r.event_type === 'status_change' && (r.old_status || r.new_status)" class="status-flow">
                    <span class="st-pill">{{ prettyStatus(r.old_status) }}</span>
                    <span class="ar">→</span>
                    <span class="st-pill to">{{ prettyStatus(r.new_status) }}</span>
                  </div>
                </td>
                <td>
                  <template v-if="r.sectors && r.sectors.length">
                    <span class="chip sector">{{ r.sectors[0] }}</span>
                    <span v-if="r.sectors.length > 1" class="chip chip-more">+{{ r.sectors.length - 1 }}</span>
                  </template>
                  <span v-else class="text-muted small">—</span>
                </td>
                <td>{{ r.region || '—' }}</td>
                <td>
                  <span class="why"><CygnusIcon name="pin" /><span v-html="whyHtml(r)"></span></span>
                </td>
                <td><span class="age" :class="{ fresh: isFresh(r) }"><CygnusIcon :name="isFresh(r) ? 'bolt' : 'clock'" />{{ ageLabel(r) }}</span></td>
                <td>
                  <div class="row-actions">
                    <button v-if="r.status !== 'actioned'" class="btn ch-btn-primary btn-sm" :disabled="busy" @click="addToPipeline(r)">
                      <CygnusIcon name="plus" /> Add to pipeline
                    </button>
                    <span v-else class="ch-inpipe"><CygnusIcon name="check" /> Actioned</span>
                    <button v-if="r.status !== 'dismissed'" class="btn ch-btn-secondary btn-sm" :disabled="busy" @click="dismiss(r)">Dismiss</button>
                  </div>
                </td>
              </tr>

              <!-- Drill-down -->
              <tr v-if="expanded === r.signal_id" class="drill">
                <td colspan="8" style="padding:0">
                  <div class="drill__inner">
                    <div>
                      <h4>Registered company</h4>
                      <div class="facts">
                        <div class="fact"><div class="fact__k">Company number</div><div class="fact__v">{{ r.company_number }}</div></div>
                        <div class="fact"><div class="fact__k">{{ r.event_type === 'incorporated' ? 'Incorporated' : 'Event date' }}</div><div class="fact__v">{{ fmtDate(r.occurred_at) }}</div></div>
                        <div class="fact wide"><div class="fact__k">Registered office</div><div class="fact__v">{{ r.address || '— not on the register event —' }}</div></div>
                        <div class="fact"><div class="fact__k">Current status</div><div class="fact__v">{{ prettyStatus(r.company_status) || '—' }}</div></div>
                      </div>

                      <h4>SIC codes</h4>
                      <div v-if="r.sic_codes && r.sic_codes.length" class="sic-list">
                        <div v-for="(code, i) in r.sic_codes" :key="code" class="sic-item">
                          <span class="code">{{ code }}</span>
                          <span class="lbl">{{ r.sectors[i] || sicLabel(r, code) || 'Registered activity' }}</span>
                          <span v-if="industryMatched(r)" class="tgt">Targeted</span>
                        </div>
                      </div>
                      <div v-else class="text-muted small">No SIC codes on the register event.</div>

                      <h4>Register history</h4>
                      <div class="timeline2">
                        <div class="tl2 now">
                          <div class="tl2__t">{{ r.event_type === 'incorporated' ? 'Incorporated' : 'Status changed' }}</div>
                          <div class="tl2__m">{{ fmtDate(r.occurred_at) }} · {{ r.event_type === 'incorporated' ? 'first appearance on the register' : (prettyStatus(r.old_status) + ' → ' + prettyStatus(r.new_status)) }}</div>
                        </div>
                        <div class="tl2">
                          <div class="tl2__t">Signal raised to you</div>
                          <div class="tl2__m">{{ fmtDateTime(r.created) }} · matched your targeting on the daily stream</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4>Why this reached you</h4>
                      <div class="explain">
                        <div class="lead"><CygnusIcon name="pin" /> Matches your fit criteria</div>
                        This company matched the targeting you set — that's why it surfaced here the same week
                        it appeared on the register.
                        <div v-if="matchedCriteria(r).length" class="crit-tags">
                          <span v-for="c in matchedCriteria(r)" :key="c.id + c.name" class="crit-tag">
                            <CygnusIcon name="check" />{{ (c.category === 'geo' ? 'Region: ' : 'Sector: ') + c.name }}
                          </span>
                        </div>
                      </div>

                      <h4>Links</h4>
                      <div style="margin-bottom:16px">
                        <a :href="chLink(r.company_number)" target="_blank" rel="noopener" class="ext-link">
                          View on Companies House <CygnusIcon name="external-link" />
                        </a>
                      </div>

                      <div class="drill__cta">
                        <button v-if="r.status !== 'actioned'" class="btn ch-btn-primary" :disabled="busy" @click="addToPipeline(r)">
                          <CygnusIcon name="plus" /> Add to pipeline
                        </button>
                        <template v-if="r.status !== 'actioned'">
                          <select v-model="drillFlowId" class="ch-select ch-select--sm">
                            <option :value="null">Enrol in drip flow…</option>
                            <option v-for="f in activeFlows" :key="f.id" :value="f.id">{{ f.name }}</option>
                          </select>
                          <button class="btn ch-btn-outline" :disabled="busy || !drillFlowId" @click="addToPipeline(r, drillFlowId)">Enrol</button>
                        </template>
                        <span v-else class="ch-inpipe"><CygnusIcon name="check" /> Actioned</span>
                        <button v-if="r.status !== 'dismissed'" class="btn btn-ghost btn-sm" :disabled="busy" @click="dismiss(r)">Dismiss</button>
                      </div>

                      <div class="honest">
                        <CygnusIcon name="info-circle" />
                        <span><strong>Brand-new companies are thin on detail.</strong> There's usually no
                          website, phone or named contact on day one — just the incorporation and the registered
                          office. Enrichment fills these in over the following days as the company sets up. Enrol
                          now to be first, and Yorlin will attach contacts as they appear.</span>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Phase C — day-zero welcome mail: post a mailer to a company the day it
           matches your targeting. Lives here (the day-zero feed) as the most
           discoverable home; the rule manager is self-contained. -->
      <DayZeroMail v-if="hasCriteria && directMail" />
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiKpiCard from "@root/components/ui/MusubiKpiCard.vue";
import DayZeroMail from "@root/components/DayZeroMail.vue";

/**
 * AdminCompaniesHouse — the tenant-facing Companies House day-zero feed
 * (CH-stream slice 2b). Mirrors AdminWebsiteVisitors.vue: a signals list with a
 * New / Actioned / Dismissed lifecycle, an expandable drill-down (register facts +
 * SIC codes + matched-on evidence + external CH link), and commit actions (Add to
 * pipeline / Enrol in drip flow) that reuse the prospect + drip enrolment path.
 * Three honest states behind the entitled view: the violet Growth teaser (server
 * 403s anyway), the "no targeting" nudge (has_criteria=false), and the
 * empty-but-configured state. Operators additionally see a stream-key management +
 * health card (Ops::PLATFORM).
 */
export default {
  name: "AdminCompaniesHouse",
  components: { MusubiKpiCard, DayZeroMail },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      gateLocked: null, // null=loading, true=teaser, false=entitled
      isOperator: false,
      // Whether this workspace has Direct Mail (/me/usage features.direct_mail);
      // the day-zero welcome mail panel is Direct Mail, so it shows only then.
      directMail: false,
      loading: false,
      busy: false,
      error: null,
      message: "",
      tab: "new",
      tabs: [
        { key: "new", label: "New", countKey: "new" },
        { key: "actioned", label: "Actioned", countKey: "actioned" },
        { key: "dismissed", label: "Dismissed", countKey: "dismissed" },
      ],
      filters: { event_type: "", sector: "", region: "", search: "" },
      kpis: { new_incorporations_7d: 0, status_changes_7d: 0, actioned: 0, avg_age_days: null },
      counts: { all: 0, new: 0, actioned: 0, dismissed: 0 },
      rows: [],
      total: 0,
      hasCriteria: true,
      expanded: null,
      drillFlowId: null,
      flows: [],
      // Operator key + health
      health: null,
      keyInput: "",
      opBusy: false,
      opMessage: "",
    };
  },
  computed: {
    avgAgeDisplay() {
      return this.kpis.avg_age_days !== null ? `${this.kpis.avg_age_days} days` : "—";
    },
    activeFlows() {
      return this.flows.filter((f) => f.status === "active" || f.status === "draft");
    },
    sectorOptions() {
      const set = new Set();
      this.rows.forEach((r) => (r.sectors || []).forEach((s) => s && set.add(s)));
      return [...set].sort();
    },
    regionOptions() {
      return [...new Set(this.rows.map((r) => r.region).filter(Boolean))].sort();
    },
    visibleRows() {
      return this.rows.filter((r) => {
        if (this.filters.sector && !(r.sectors || []).includes(this.filters.sector)) return false;
        if (this.filters.region && r.region !== this.filters.region) return false;
        return true;
      });
    },
  },
  created() {
    this.checkGate();
  },
  methods: {
    async checkGate() {
      try {
        const res = await this.api.get("/api/v1/me/usage");
        const body = res?.data ?? res;
        const entitled = !!body?.features?.ch_stream || !!body?.plan?.can_manage;
        this.isOperator = !!body?.plan?.can_manage;
        this.directMail = !!body?.features?.direct_mail;
        this.gateLocked = !entitled;
      } catch (e) {
        // Fail-open: a usage blip shouldn't lock an entitled tenant out. The
        // server-side gate is still authoritative on every data read.
        this.gateLocked = false;
      }
      if (this.gateLocked === false) {
        this.loadFlows();
        if (this.isOperator) this.loadHealth();
        this.load();
      }
    },
    async load() {
      this.loading = true;
      this.error = null;
      this.expanded = null;
      try {
        const qs = new URLSearchParams({
          status: this.tab,
          event_type: this.filters.event_type,
          search: this.filters.search,
          per_page: "100",
        });
        const res = await this.api.get("/api/v1/admin/ch-stream/signals?" + qs.toString());
        const body = res?.data ?? res;
        this.rows = Array.isArray(body?.rows) ? body.rows : [];
        this.counts = body?.counts || this.counts;
        this.kpis = body?.kpis || this.kpis;
        this.hasCriteria = body?.has_criteria !== false;
        this.total = Number(body?.total) || this.rows.length;
      } catch (e) {
        this.error = this.errText(e, "Could not load Companies House signals.");
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    async loadFlows() {
      try {
        const res = await this.api.get("/api/v1/admin/drip-flows");
        const p = res?.data?.data ?? res?.data ?? res;
        this.flows = Array.isArray(p) ? p : (p?.rows || []);
      } catch (e) { /* enrol dropdown just stays empty — not fatal */ }
    },
    async loadHealth() {
      try {
        const res = await this.api.get("/api/v1/admin/ch-stream/config");
        this.health = res?.data ?? res;
      } catch (e) { this.health = null; }
    },
    async saveKey() {
      if (!this.keyInput.trim()) return;
      this.opBusy = true; this.opMessage = ""; this.error = null;
      try {
        const res = await this.api.post("/api/v1/admin/ch-stream/config", { key: this.keyInput.trim() });
        this.health = res?.data ?? res;
        this.keyInput = "";
        this.opMessage = "Streaming key saved.";
      } catch (e) {
        this.error = this.errText(e, "Could not save the streaming key.");
      } finally {
        this.opBusy = false;
      }
    },
    async clearKey() {
      this.opBusy = true; this.opMessage = ""; this.error = null;
      try {
        const res = await this.api.delete("/api/v1/admin/ch-stream/config");
        this.health = res?.data ?? res;
        this.opMessage = "Override cleared — using the environment key (if set).";
      } catch (e) {
        this.error = this.errText(e, "Could not clear the override.");
      } finally {
        this.opBusy = false;
      }
    },
    setTab(key) {
      if (this.tab === key) return;
      this.tab = key;
      this.load();
    },
    toggleExpand(signalId) {
      this.expanded = this.expanded === signalId ? null : signalId;
      this.drillFlowId = null;
    },
    async addToPipeline(row, flowId = null) {
      this.busy = true; this.error = null; this.message = "";
      try {
        const body = flowId ? { drip_flow_id: Number(flowId) } : {};
        const res = await this.api.post("/api/v1/admin/ch-stream/signals/" + row.signal_id + "/pipeline", body);
        const out = res?.data ?? res;
        if (out?.ok) {
          this.message = flowId ? "Enrolled in the drip flow." : "Added to your pipeline.";
          await this.load();
          if (this.isOperator) this.loadHealth();
        } else {
          this.error = out?.error || "Could not add to pipeline.";
        }
      } catch (e) {
        this.error = this.errText(e, "Could not add to pipeline.");
      } finally {
        this.busy = false;
      }
    },
    async dismiss(row) {
      this.busy = true; this.error = null;
      try {
        await this.api.post("/api/v1/admin/ch-stream/signals/" + row.signal_id + "/dismiss", {});
        this.message = "Signal dismissed.";
        await this.load();
      } catch (e) {
        this.error = this.errText(e, "Could not dismiss.");
      } finally {
        this.busy = false;
      }
    },
    // ── evidence helpers ──
    matchedCriteria(row) {
      const c = row?.matched_on?.criteria;
      return Array.isArray(c) ? c : [];
    },
    industryMatched(row) {
      return this.matchedCriteria(row).some((c) => c.category !== "geo");
    },
    whyHtml(row) {
      const crit = this.matchedCriteria(row);
      if (crit.length) {
        const names = crit.map((c) => this.escape(c.name)).join(" · ");
        return `Matches your targeting: <b>${names}</b>`;
      }
      const bits = [];
      if (row.sectors && row.sectors.length) bits.push(this.escape(row.sectors[0]));
      if (row.region) bits.push(this.escape(row.region));
      return bits.length ? `Matches your targeting: <b>${bits.join(" · ")}</b>` : "Matches your targeting";
    },
    sicLabel(row, code) {
      const i = (row.sic_codes || []).indexOf(code);
      return i >= 0 ? (row.sectors || [])[i] : "";
    },
    // ── formatting helpers ──
    prettyStatus(s) {
      if (!s) return "";
      return String(s).replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    incorpLabel(row) {
      if (row.event_type === "incorporated") return "Incorporated " + this.fmtDate(row.occurred_at);
      return "Registered company";
    },
    isFresh(row) {
      if (!row.occurred_at) return false;
      const days = (Date.now() / 1000 - Number(row.occurred_at)) / 86400;
      return days <= 2;
    },
    ageLabel(row) {
      const epoch = Number(row.occurred_at);
      if (!epoch) return "—";
      const verb = row.event_type === "incorporated" ? "Incorporated" : "Changed";
      const s = Math.max(0, Math.floor(Date.now() / 1000) - epoch);
      const days = Math.floor(s / 86400);
      if (days <= 0) return verb + " today";
      if (days === 1) return "Yesterday";
      if (days < 30) return days + " days ago";
      return this.fmtDate(epoch);
    },
    fmtDate(epoch) {
      if (!epoch) return "—";
      return new Date(Number(epoch) * 1000).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
    },
    fmtDateTime(epoch) {
      if (!epoch) return "—";
      const d = new Date(Number(epoch) * 1000);
      return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) + ", " +
        d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    relTime(epoch) {
      if (!epoch) return "—";
      const s = Math.max(0, Math.floor(Date.now() / 1000) - Number(epoch));
      if (s < 3600) return Math.max(1, Math.floor(s / 60)) + "m ago";
      if (s < 86400) return Math.floor(s / 3600) + "h ago";
      if (s < 172800) return "Yesterday";
      if (s < 604800) return Math.floor(s / 86400) + " days ago";
      return new Date(Number(epoch) * 1000).toLocaleDateString();
    },
    chLink(number) {
      return "https://find-and-update.company-information.service.gov.uk/company/" + encodeURIComponent(number || "");
    },
    escape(s) {
      return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      if (body?.error === "plan_gated") return body.message || fallback;
      return body?.message || body?.error || fallback;
    },
  },
};
</script>

<style scoped>
.musubi-page--chfeed { max-width: 1240px; }
.musubi-page__head h1 { font-size: 1.6rem; font-weight: 700; margin: 0 0 6px; display: flex; align-items: center; gap: 10px; }
.musubi-page__head p { margin: 0; max-width: 760px; line-height: 1.55; font-size: 0.92rem; }
.text-muted { color: var(--m-text-secondary); }
.plan-pill { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--m-teal-text, #0F766E); background: var(--m-teal-soft, #E4F7F4); border: 1px solid var(--m-teal-line, #B6EBE4); border-radius: 999px; padding: 3px 9px; position: relative; top: -2px; }

.coverage-note { display: flex; align-items: flex-start; gap: 9px; margin: 16px 0 8px; font-size: 0.8rem; color: var(--m-text-secondary); line-height: 1.5; }
.coverage-note i { color: var(--m-status-info); margin-top: 2px; }

/* Operator card row */
.ch-ops { display: grid; grid-template-columns: 1.4fr 1fr; gap: 14px; margin: 4px 0 20px; }
@media (max-width: 900px) { .ch-ops { grid-template-columns: 1fr; } }
.ch-ops__card { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); padding: 14px 16px; box-shadow: var(--m-shadow-card); }
.ch-ops__head { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 0.9rem; margin-bottom: 12px; }
.ch-ops__head i { color: var(--m-accent); }
.ch-ops__badge { margin-left: auto; font-size: 0.62rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; border-radius: 999px; padding: 2px 8px; }
.ch-ops__badge.on { background: var(--m-teal-soft, #E4F7F4); color: var(--m-teal-text, #0F766E); }
.ch-ops__badge.off { background: #F1F3F7; color: #697086; }
.ch-ops__row { display: flex; align-items: baseline; gap: 10px; font-size: 0.84rem; margin-bottom: 10px; }
.ch-ops__k { color: var(--m-text-tertiary); font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; min-width: 82px; }
.ch-ops__v code { font-size: 0.82rem; letter-spacing: 1px; }
.ch-src { font-size: 0.68rem; color: var(--m-text-tertiary); margin-left: 8px; }
.ch-ops__setline { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin: 4px 0 8px; }
.ch-ops__note { font-size: 0.74rem; color: var(--m-text-tertiary); line-height: 1.5; margin: 0 0 6px; }
.ch-ops__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 18px; }
.ch-health__k { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); font-weight: 600; margin-bottom: 2px; }
.ch-health__v { font-weight: 700; font-size: 0.95rem; font-variant-numeric: tabular-nums; }

.ch-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 8px 0; }
@media (max-width: 1024px) { .ch-kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px) { .ch-kpis { grid-template-columns: 1fr; } }

.ch-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--m-border); margin: 22px 0 0; }
.ch-tab { padding: 9px 16px; font-size: 0.86rem; font-weight: 500; color: var(--m-text-secondary); border: none; background: none; border-bottom: 2px solid transparent; cursor: pointer; }
.ch-tab.active { color: var(--m-accent); border-bottom-color: var(--m-accent); font-weight: 600; }
.ch-tab .count { font-size: 0.7rem; background: var(--m-accent-soft); color: var(--m-accent-hover); padding: 1px 7px; border-radius: 999px; margin-left: 6px; font-weight: 700; }

.ch-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 16px 0; }
.ch-select, .ch-input { border: 1px solid var(--m-border); background: var(--m-bg-card); border-radius: 6px; font-size: 0.82rem; padding: 6px 10px; color: var(--m-text-primary); font-family: inherit; }
.ch-select--sm { padding: 5px 8px; font-size: 0.78rem; }

.btn { font-family: inherit; font-weight: 500; font-size: 0.82rem; padding: 0.4rem 0.85rem; border-radius: 6px; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; text-decoration: none; line-height: 1.3; white-space: nowrap; }
.btn:disabled { opacity: 0.5; cursor: default; }
.btn-sm { font-size: 0.76rem; padding: 0.3rem 0.6rem; }
.ch-btn-primary { background: var(--m-accent); border-color: var(--m-accent); color: #fff; }
.ch-btn-primary:hover:not(:disabled) { background: var(--m-accent-hover); border-color: var(--m-accent-hover); }
.ch-btn-outline { color: var(--m-accent-hover); border-color: var(--m-accent); background: var(--m-bg-card); }
.ch-btn-outline:hover:not(:disabled) { background: var(--m-accent); color: #fff; }
.ch-btn-secondary { color: var(--m-text-secondary); border-color: var(--m-border); background: var(--m-bg-card); }
.ch-btn-secondary:hover:not(:disabled) { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-text-primary); }
.btn-ghost { background: none; border: none; color: var(--m-text-secondary); padding: 6px 8px; cursor: pointer; border-radius: 6px; }
.btn-ghost:hover:not(:disabled) { background: var(--m-rail-hover-bg); color: var(--m-text-primary); }
.ch-inpipe { display: inline-flex; align-items: center; gap: 5px; font-size: 0.78rem; font-weight: 600; color: var(--m-teal-text, #0F766E); }

.ch-loading { padding: 32px; text-align: center; color: var(--m-text-secondary); }

.ch-tbl { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); overflow: hidden; }
.ch-tbl table { width: 100%; border-collapse: collapse; }
.ch-tbl thead th { background: rgba(79, 70, 229, 0.07); border-bottom: 1px solid var(--m-border); text-align: left; font-size: 0.66rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: var(--m-text-secondary); padding: 11px 14px; white-space: nowrap; }
.ch-tbl tbody td { padding: 0.7rem 0.85rem; font-size: 0.85rem; border-bottom: 1px solid var(--m-border); vertical-align: middle; }
.ch-tbl tbody tr.is-open { background: rgba(20, 184, 166, 0.05); }
.co-name { font-weight: 600; color: var(--m-text-primary); }
.co-meta { font-size: 0.76rem; color: var(--m-text-secondary); display: flex; align-items: center; gap: 8px; margin-top: 3px; flex-wrap: wrap; }
.co-meta .num { font-variant-numeric: tabular-nums; font-weight: 600; color: var(--m-text-tertiary); }
.co-meta .sep { color: var(--m-border-strong); }
.chev { background: none; border: none; color: var(--m-text-tertiary); cursor: pointer; padding: 2px; display: inline-flex; transition: transform 0.15s; }
tr.is-open .chev { transform: rotate(90deg); color: var(--m-teal, #14B8A6); }
.chip { display: inline-block; padding: 0.16rem 0.55rem; font-size: 0.72rem; font-weight: 600; border-radius: 5px; background: #EEF1F6; color: #556074; white-space: nowrap; }
.chip-more { background: transparent; color: var(--m-text-tertiary); font-weight: 600; }

.ev-badge { display: inline-flex; align-items: center; gap: 6px; padding: 0.18rem 0.55rem; font-size: 0.72rem; font-weight: 700; border-radius: 5px; white-space: nowrap; text-transform: uppercase; letter-spacing: 0.03em; }
.ev-badge.incorporated { background: var(--m-teal-soft, #E4F7F4); color: var(--m-teal-text, #0F766E); border: 1px solid var(--m-teal-line, #B6EBE4); }
.ev-badge.incorporated .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--m-teal, #14B8A6); }
.ev-badge.status { background: #F1F3F7; color: #556074; border: 1px solid var(--m-border-strong); }
.ev-badge.status .dot { width: 6px; height: 6px; border-radius: 50%; background: #8A93A6; }
.status-flow { display: inline-flex; align-items: center; gap: 6px; margin-top: 5px; font-size: 0.72rem; }
.st-pill { padding: 0.1rem 0.45rem; border-radius: 4px; background: #F1F3F7; color: #697086; font-weight: 600; }
.st-pill.to { background: #FFF4E6; color: #9A5B12; border: 1px solid #F0DCBB; }
.status-flow .ar { color: var(--m-text-tertiary); }

.why { display: inline-flex; align-items: flex-start; gap: 7px; font-size: 0.8rem; color: var(--m-text-primary); max-width: 260px; line-height: 1.4; }
.why i { color: var(--m-teal, #14B8A6); margin-top: 2px; }

.age { font-size: 0.82rem; display: inline-flex; align-items: center; gap: 6px; color: var(--m-text-secondary); white-space: nowrap; }
.age i { color: var(--m-text-tertiary); }
.age.fresh { color: var(--m-teal-text, #0F766E); font-weight: 600; }
.age.fresh i { color: var(--m-teal, #14B8A6); }
.row-actions { display: flex; align-items: center; gap: 6px; justify-content: flex-end; }

.drill { background: #FBFCFE; }
.drill__inner { display: grid; grid-template-columns: 1.15fr 1fr; gap: 26px; padding: 22px 26px; }
@media (max-width: 900px) { .drill__inner { grid-template-columns: 1fr; } }
.drill h4 { margin: 0 0 12px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--m-text-secondary); }
.facts { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 18px; margin-bottom: 18px; }
.fact { font-size: 0.82rem; }
.fact.wide { grid-column: 1 / -1; }
.fact__k { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); font-weight: 600; margin-bottom: 3px; }
.fact__v { font-weight: 600; }
.sic-list { display: grid; gap: 6px; margin-bottom: 18px; }
.sic-item { display: flex; align-items: baseline; gap: 9px; font-size: 0.82rem; padding: 6px 10px; background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 8px; }
.sic-item .code { font-variant-numeric: tabular-nums; font-weight: 700; color: var(--m-accent-hover); flex: none; }
.sic-item .lbl { color: var(--m-text-primary); }
.sic-item .tgt { margin-left: auto; flex: none; font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--m-teal-text, #0F766E); background: var(--m-teal-soft, #E4F7F4); border: 1px solid var(--m-teal-line, #B6EBE4); padding: 1px 6px; border-radius: 4px; }
.timeline2 { position: relative; padding-left: 20px; margin-bottom: 6px; }
.timeline2::before { content: ""; position: absolute; left: 5px; top: 5px; bottom: 5px; width: 2px; background: var(--m-border-strong); }
.tl2 { position: relative; padding: 0 0 14px; }
.tl2::before { content: ""; position: absolute; left: -18px; top: 4px; width: 10px; height: 10px; border-radius: 50%; background: var(--m-bg-card); border: 2px solid var(--m-text-tertiary); }
.tl2.now::before { background: var(--m-teal, #14B8A6); border-color: var(--m-teal, #14B8A6); }
.tl2__t { font-weight: 600; font-size: 0.84rem; }
.tl2__m { font-size: 0.74rem; color: var(--m-text-secondary); margin-top: 1px; }
.explain { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 10px; padding: 14px 16px; font-size: 0.85rem; line-height: 1.55; margin-bottom: 16px; }
.explain .lead { font-weight: 600; display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.explain .lead i { color: var(--m-teal, #14B8A6); }
.crit-tags { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
.crit-tag { display: inline-flex; align-items: center; gap: 6px; font-size: 0.74rem; font-weight: 600; background: var(--m-teal-soft, #E4F7F4); color: var(--m-teal-text, #0F766E); border: 1px solid var(--m-teal-line, #B6EBE4); border-radius: 999px; padding: 3px 10px; }
.ext-link { display: inline-flex; align-items: center; gap: 6px; font-size: 0.82rem; font-weight: 600; color: var(--m-accent-hover); text-decoration: none; }
.ext-link:hover { text-decoration: underline; }
.drill__cta { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-top: 4px; }
.honest { display: flex; gap: 9px; align-items: flex-start; margin-top: 14px; padding: 11px 13px; background: #F6F8FC; border: 1px solid var(--m-border); border-radius: 10px; font-size: 0.79rem; color: var(--m-text-secondary); line-height: 1.5; }
.honest i { color: var(--m-status-info); margin-top: 1px; }

/* Empty / nudge states */
.dc-empty { text-align: center; padding: 48px 24px; color: var(--m-text-secondary); background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); }
.dc-empty.teal-edge { border-top: 3px solid var(--m-teal, #14B8A6); }
.dc-empty__icon { display: inline-flex; align-items: center; justify-content: center; width: 64px; height: 64px; border-radius: 50%; background: var(--m-accent-soft); color: var(--m-accent-hover); font-size: 24px; margin-bottom: 16px; }
.dc-empty__icon.teal { background: var(--m-teal-soft, #E4F7F4); color: var(--m-teal-text, #0F766E); }
.dc-empty__title { font-weight: 600; color: var(--m-text-primary); font-size: 1rem; margin-bottom: 8px; }
.dc-empty__hint { font-size: 0.86rem; max-width: 540px; margin: 0 auto 8px; line-height: 1.6; }
.dc-empty__why { max-width: 540px; margin: 14px auto 0; text-align: left; display: grid; gap: 8px; }
.dc-empty__why .row { display: flex; gap: 9px; align-items: flex-start; font-size: 0.82rem; line-height: 1.45; }
.dc-empty__why .row i { color: var(--m-status-info); margin-top: 3px; }
.dc-empty__actions { margin-top: 22px; display: flex; gap: 10px; justify-content: center; }

/* Violet Starter teaser */
.ch-teaser { position: relative; border-radius: var(--m-radius-lg, 14px); overflow: hidden; border: 1px solid var(--m-border); margin: 20px 0; min-height: 340px; }
.ch-teaser__blur { filter: blur(4px); opacity: 0.55; pointer-events: none; user-select: none; padding: 8px; }
.ch-skel-tbl { background: var(--m-bg-card); }
.ch-skel-row { display: flex; gap: 18px; align-items: center; padding: 16px 18px; border-bottom: 1px solid var(--m-border); }
.skel { height: 11px; border-radius: 5px; background: linear-gradient(90deg, #E9ECF3, #F3F5F9, #E9ECF3); display: inline-block; }
.ch-teaser__panel { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, rgba(243, 238, 254, 0.72), rgba(251, 250, 255, 0.82)); }
.upgrade { max-width: 530px; text-align: center; background: linear-gradient(135deg, var(--m-violet-soft, #F3EEFE), #FBFAFF); border: 1px solid var(--m-violet-line, #E0D2FB); border-radius: 16px; padding: 30px 34px; box-shadow: var(--m-shadow-panel); }
.upgrade__ic { width: 52px; height: 52px; border-radius: 14px; background: var(--m-bg-card); border: 1px solid var(--m-violet-line, #E0D2FB); color: var(--m-violet, #7C3AED); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; margin: 0 auto 14px; }
.upgrade__eyebrow { font-size: 0.68rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--m-violet, #7C3AED); margin-bottom: 8px; }
.upgrade h3 { margin: 0 0 8px; font-size: 1.25rem; }
.upgrade p { margin: 0 0 18px; font-size: 0.88rem; color: var(--m-text-secondary); line-height: 1.55; }
.upgrade__benefits { text-align: left; display: grid; gap: 10px; margin: 0 auto 22px; max-width: 400px; }
.benefit { display: flex; gap: 10px; align-items: flex-start; font-size: 0.86rem; line-height: 1.4; }
.benefit i { color: var(--m-violet, #7C3AED); margin-top: 3px; }
.upgrade__actions { display: flex; gap: 10px; justify-content: center; align-items: center; }
.ch-btn-violet { background: var(--m-violet, #7C3AED); border-color: var(--m-violet, #7C3AED); color: #fff; }
.ch-btn-violet:hover { background: #6D28D9; }
.ch-compare { font-size: 0.85rem; font-weight: 700; color: var(--m-violet, #7C3AED); text-decoration: none; display: inline-flex; align-items: center; gap: 5px; }
.ch-compare:hover { text-decoration: underline; }
</style>
